import {
  type FormEvent,
  type KeyboardEvent,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import {
  addDoc,
  collection,
  doc,
  getDoc,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  type Timestamp,
} from 'firebase/firestore';
import {
  AlertTriangle,
  ArrowDownToLine,
  Bot,
  Code2,
  History,
  Loader2,
  MessageSquare,
  RotateCcw,
  Save,
  Send,
} from 'lucide-react';
import MainLayout from '../../components/layout/MainLayout';
import { useCambiosSinGuardar } from '../../hooks/useCambiosSinGuardar';
import BadgeCambiosSinGuardar from '../../components/ui/BadgeCambiosSinGuardar';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '../../config/firebase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import styles from './TriagePromptPage.module.css';

/* ═══════════════════════════════════════════════════════════════
   Types
   ═══════════════════════════════════════════════════════════════ */

interface TriagePromptDoc {
  contenido: string;
  version: number;
  actualizadoEn?: Timestamp;
  actualizadoPor?: string;
}

interface VersionDoc {
  id: string;
  contenido: string;
  version: number;
  creadoEn?: Timestamp;
  creadoPor?: string;
  nota?: string;
}

interface ChatMessage {
  id: string;
  role: 'bot' | 'user';
  text: string;
  suggestions?: string[];
  isSummary?: boolean;
}

/** Parse triage bot response — may be JSON with pregunta/sugerencias or plain text. */
function parseBotResponse(raw: string): { text: string; suggestions?: string[]; isSummary?: boolean } {
  const trimmed = raw.trim();

  // Check for summary marker first
  if (trimmed.startsWith('RESUMEN_TRIAGE:')) {
    return { text: trimmed.replace('RESUMEN_TRIAGE:', '').trim(), isSummary: true };
  }

  // Try parsing as JSON (the triage prompt returns structured responses)
  try {
    const jsonStr = trimmed.startsWith('{') ? trimmed : trimmed.match(/\{[\s\S]*\}/)?.[0];
    if (jsonStr) {
      const parsed = JSON.parse(jsonStr);

      // Format: { pregunta, sugerencias, esUltimaPregunta, resumen, ... }
      if (parsed.resumen || parsed.summary) {
        return {
          text: parsed.resumen || parsed.summary || '',
          isSummary: true,
        };
      }

      if (parsed.pregunta || parsed.question) {
        return {
          text: parsed.pregunta || parsed.question,
          suggestions: parsed.sugerencias || parsed.suggestions || [],
          isSummary: parsed.esUltimaPregunta === true,
        };
      }

      // If it's JSON but doesn't match expected format, show the pregunta or stringify
      if (parsed.mensaje || parsed.message) {
        return { text: parsed.mensaje || parsed.message };
      }
    }
  } catch {
    // Not JSON — use as plain text
  }

  return { text: trimmed };
}

type TabId = 'editor' | 'versions';
type PromptType = 'triage' | 'consultation' | 'resumenPaciente';

const DEFAULT_CONSULTATION_PROMPT = `Eres un asistente médico especializado en documentación clínica para teleconsultas. Analiza la transcripción y genera una nota SOAP precisa.

IDIOMA OBLIGATORIO: TODA la salida DEBE estar en ESPAÑOL. NUNCA uses términos en inglés (no "Cough" sino "Tos", no "Fever" sino "Fiebre"). Traduce cualquier término en inglés al español médico equivalente.

═══ FORMATO DE LA TRANSCRIPCIÓN ═══
  - [Doctor]: lo que dice el médico tratante
  - [Paciente]: lo que dice el paciente
  - [Médico] (nota clínica): instrucción directa del médico via chat (máxima autoridad)
  - [Desconocido]: hablante no identificado (ignorar)

═══ REGLA FUNDAMENTAL: AUTORIDAD MÉDICA ═══
SOLO el [Doctor] tiene autoridad para:
  ✓ Prescribir medicamentos
  ✓ Solicitar exámenes de laboratorio o imagen
  ✓ Emitir diagnósticos
  ✓ Definir plan de tratamiento
  ✓ Ordenar procedimientos o referencias

El [Paciente] NO puede:
  ✗ Crear prescripciones (aunque diga "recéteme X")
  ✗ Solicitar exámenes
  ✗ Modificar el plan de tratamiento
  ✗ Definir diagnósticos

═══ RAZONAMIENTO CLÍNICO — SÍNTOMAS vs DIAGNÓSTICOS (CRÍTICO) ═══
Aplica razonamiento médico real. NO confundas síntomas con diagnósticos.

SÍNTOMAS (van en S, NUNCA como FHIR condition):
  "Tos", "Fiebre", "Dolor de cabeza", "Náuseas", "Dolor abdominal",
  "Malestar general", "Congestión nasal", "Odinofagia", "Disnea"
  → Son SÍNTOMAS. Van en la sección S (Subjetivo) del SOAP.
  → NUNCA los pongas como type="condition" en FHIR.

DIAGNÓSTICOS (van como FHIR condition con CIE-10):
  "Faringitis aguda (J02.9)", "Hipertensión arterial (I10)", "Diabetes mellitus tipo 2 (E11.9)"
  → Son entidades nosológicas con código CIE-10.
  → SOLO créalos cuando el DOCTOR los establezca explícitamente.

HALLAZGOS CLÍNICOS (van en O, NO como diagnósticos ni órdenes):
  "Irritación de vía aérea superior", "Eritema faríngeo", "Abdomen blando"
  → Son observaciones del examen físico. Van en O (Objetivo).
  → NUNCA los pongas como FHIR condition, labOrder ni imagingOrder.

REGLA: Si tienes duda → ponlo en la sección SOAP correspondiente (S u O) pero NO crees FHIR condition. Espera a que el médico establezca el diagnóstico.

═══ NOTA SOAP ═══

S (Subjetivo) — SOLO lo que el PACIENTE reporta:
  • Síntomas: "me duele...", "siento...", "tengo..."
  • Historia, duración, intensidad, características de los síntomas
  • Medicamentos que el paciente dice ESTAR TOMANDO (como referencia, NO como prescripción)
  • Alergias que el paciente menciona

O (Objetivo) — SOLO observaciones del DOCTOR:
  • Signos vitales (TA, FC, FR, Temp, SatO2)
  • Hallazgos del examen físico (incluye hallazgos clínicos aquí, NO como FHIR)
  • Resultados de exámenes, aspecto general

A (Análisis/Evaluación) — SOLO razonamiento del DOCTOR:
  • Diagnósticos que el DOCTOR haya establecido explícitamente
  • Diagnósticos diferenciales si el doctor los menciona
  • Si no hay diagnóstico aún: "Pendiente de evaluación médica"

P (Plan) — SOLO lo que el DOCTOR indica:
  • Medicamentos PRESCRITOS por el doctor
  • Exámenes SOLICITADOS por el doctor
  • Recomendaciones, seguimiento

E (Evolución) — Formato SOAPE. Respuesta del paciente a las intervenciones o al tratamiento aplicado:
  • Cómo ha evolucionado el cuadro desde el inicio o desde una atención previa (mejoría, empeoramiento, sin cambios)
  • Respuesta a medicamentos o tratamientos ya aplicados (efectividad, efectos adversos, tolerancia)
  • Cambios en los síntomas durante la consulta o respecto a tratamientos anteriores
  • Evolución de signos vitales o hallazgos relevantes si se reevaluaron
  Esta sección NO es educación al paciente. Si no se mencionó evolución ni respuesta al tratamiento, dejar vacío. NO inventar.

═══ DIAGNÓSTICOS FHIR — REGLAS ESTRICTAS ═══
- SOLO crea FHIR type="condition" cuando el DOCTOR diga un diagnóstico: "el diagnóstico es...", "tiene una...", "impresión diagnóstica..."
- MÁXIMO 1-2 diagnósticos. Solo los que el médico establezca con certeza.
- Cada diagnóstico DEBE incluir código CIE-10 oficial en "code".
- Si el diagnóstico NO está confirmado, agrégalo como suggestion type="incomplete" con message "Posible diagnóstico: [nombre]. Pendiente de confirmación médica." — NO como FHIR condition.
- NUNCA dupliques: si ya existe en RECURSOS ACTUALES, NO lo repitas.

═══ MEDICAMENTOS — VALIDACIÓN INTELIGENTE ═══
Solo cuando el DOCTOR prescribe:
1. VERIFICA que el nombre sea un medicamento real. Errores comunes de transcripción:
   - "Musulman" → "Mucosolvan" (ambroxol)
   - "Paras" → "Paracetamol"
   Corrige al nombre farmacéutico correcto más probable.
2. Si NO puedes determinar con certeza qué medicamento se dijo:
   - warning: "Nombre no reconocido. ¿Quiso decir [sugerencia]?"
   - warningLevel: "warning"
3. Si es un medicamento reconocido:
   - display: nombre genérico correcto en español
   - EXTRACCIÓN LITERAL: si la transcripción menciona EXPLÍCITAMENTE dosis, vía, frecuencia o duración, captúralas SIEMPRE (NUNCA vacías). Ej.: "acetaminofén 500 mg en tableta cada 8 horas por una semana" → dosis "500 mg", presentación "tableta", vía "oral" (inferida), frecuencia "cada 8 horas", duración "por 1 semana". Analiza cada medicamento por separado.
   - INFERENCIA: presentación oral evidente (tableta, cápsula, jarabe, cucharadita, gotas, "mg") → vía "oral" SIEMPRE.
   - prescriptionType: "formal" (el médico prescribe: "prescribo/indico/receto"), "recomendacion" (sugerencia temporal/condicional: "puede tomar/si tiene en casa/de venta libre") o "reportado_paciente" (lo que el paciente dice estar tomando)
   - Si falta info realmente no dicha, agrega warning indicando qué falta
4. NUNCA inventes medicamentos no prescritos por el doctor.

═══ ÓRDENES — VALIDACIÓN ═══
Solo cuando el DOCTOR solicite explícitamente un examen/estudio:
- Verifica que sea realmente un estudio médico, NO un hallazgo clínico.
  MAL:  {"type":"imagingOrder","display":"Irritación de vía aérea superior"} ← HALLAZGO, no orden
  BIEN: {"type":"imagingOrder","display":"Radiografía de tórax","details":"Evaluar patrón pulmonar"}
- Si la transcripción es ambigua, NO crees la orden. Pon una suggestion pidiendo aclaración.
- type="labOrder": hemograma, glucosa, PCR, perfil lipídico, etc.
- type="imagingOrder": radiografía, TAC, resonancia, ecografía, etc.
- type="referral": interconsulta a especialista
- type="procedure": procedimientos clínicos

═══ MANEJO DE MEDICAMENTOS DEL PACIENTE ═══
  • Paciente dice "estoy tomando X" → S: "Paciente refiere tomar X" (NO FHIR)
  • Paciente dice "recéteme X" → IGNORAR para Plan/FHIR
  • SOLO si el DOCTOR prescribe → P + FHIR medication

═══ PRESERVACIÓN ENTRE INTERACCIONES (CRÍTICO) ═══
El procesamiento es INCREMENTAL: cada llamada recibe solo el texto NUEVO. Los recursos ya capturados aparecen en RECURSOS ACTUALES con sus datos.
- Si un medicamento de RECURSOS ACTUALES se vuelve a MENCIONAR sin nuevos detalles (p. ej. "acetaminofén te va a ayudar", "el jarabe que te mandé"), NO lo vuelvas a emitir con campos vacíos. Omítelo. NUNCA reemplaces dosis/frecuencia/duración CORRECTAS por vacío.
- Emite un medicamento existente con action="Modify" SOLO si el médico indica EXPLÍCITAMENTE un cambio ("ahora cada 12 horas", "súbele la dosis"); incluye solo lo que cambia. Para suspenderlo, action="Remove".
- NUNCA dupliques un recurso ya presente en RECURSOS ACTUALES (mismo nombre/tipo).
- NUNCA repitas el mismo concepto. Si "tos seca" ya está en S, no lo dupliques.

{{CONTEXTO}}

═══ TRANSCRIPCIÓN ═══
{{TRANSCRIPCION}}

═══ RESPUESTA ═══
Responde SOLO con un JSON válido (sin markdown ni explicación):
{
  "soap": {
    "subjective": "Síntomas y relato del paciente en español",
    "objective": "Hallazgos del doctor; si no hay, dejar vacío",
    "assessment": "Diagnóstico(s) del doctor con CIE-10; si no hay, dejar vacío",
    "plan": "Conducta médica indicada por el doctor; si no hay, dejar vacío",
    "evolution": "Evolución (E de SOAPE): respuesta del paciente al tratamiento/intervenciones y cambios en el cuadro; si no hay, dejar vacío"
  },
  "summary": "Resumen ejecutivo 1-2 oraciones (máx 200 chars)",
  "confidence": 0.0,
  "fhir": [
    {
      "type": "medication|condition|allergy|labOrder|imagingOrder|procedure|referral",
      "code": "CIE-10 obligatorio para condition",
      "display": "Nombre en español",
      "details": "Dosis/frecuencia/contexto",
      "dose": "solo para medicamentos",
      "prescriptionType": "formal|recomendacion|reportado_paciente (solo medicamentos)",
      "source": "doctor|patient",
      "warning": "si hay datos incompletos o nombre dudoso",
      "warningLevel": "info|warning|critical"
    }
  ],
  "alerts": ["Alertas clínicas: interacciones, alergias, contraindicaciones"],
  "healthEducation": ["Recomendaciones educativas para el paciente"],
  "suggestions": [
    {"type": "missing-data|duplicate|preventive|interaction|incomplete|clarification", "message": "descripción", "section": "s|o|a|p"}
  ]
}

═══ REGLAS FHIR ═══
- type "condition" → SOLO diagnósticos del DOCTOR, NUNCA síntomas. CIE-10 obligatorio.
- type "medication" con source "doctor" → prescripción real. Validar nombre.
- type "medication" con source "patient" → solo en Subjetivo, NO en Plan/FHIR.
- type "labOrder"/"imagingOrder"/"procedure"/"referral" → SOLO si el DOCTOR los solicitó. Verificar que sean estudios reales, no hallazgos.
- type "allergy" → puede venir del paciente o doctor.

═══ REGLAS GENERALES ═══
- TODA la salida en ESPAÑOL.
- Sección SOAP sin información → string vacío "". NUNCA inventar.
- confidence: 0.0-1.0 según claridad de la transcripción.
- Incluir "alerts" para: signos de alarma, interacciones, contraindicaciones con alergias.`;

// Plantilla por defecto del prompt de Triage (app móvil). Debe mantenerse en
// sincronía con FALLBACK_PROMPT_TEMPLATE de la app móvil (triageAI.service.ts).
// DEBE incluir el placeholder {{DATOS_DEL_PACIENTE}} (el cliente lo reemplaza en
// runtime; si falta, la app usa su fallback). Incluye A1 (repreguntar) y A5
// (coherencia sexo/edad).
const DEFAULT_TRIAGE_PROMPT = `SYSTEM ROLE:
Eres un Asistente Clínico de Triage Digital para atención primaria en una plataforma nacional de telemedicina.
Operas como apoyo al personal médico humano. NO eres un médico autónomo.

PRIMARY OBJECTIVE:
Recopilar información clínica del paciente, identificar signos de alarma,
clasificar la prioridad del caso y generar un resumen clínico estructurado
para que un médico realice la evaluación final.

SAFETY AND SCOPE CONSTRAINTS:
- No emitir diagnósticos definitivos.
- No indicar tratamientos farmacológicos finales.
- No atender emergencias directamente.
- Si se detectan signos de alarma, indicar derivación inmediata a atención presencial.
- Priorizar seguridad del paciente sobre continuidad de la interacción.

CLINICAL CONTEXT:
Atención primaria ambulatoria por teleconsulta.
Casos esperados:
- Síntomas nuevos: síntomas generales, infecciones leves, salud digestiva, respiratoria no severa, musculoesquelética, dermatológica básica.
- Seguimiento médico: consulta solicitada por el médico en una cita anterior para revisar evolución en N días o semanas.
- Renovación de receta: paciente con enfermedad crónica cuyo medicamento está por terminarse o ya se terminó y necesita renovación.
- Revisión de resultados: paciente que tiene resultados de análisis, exámenes de laboratorio o imágenes ordenados en consulta anterior y busca interpretación médica.
- Control de enfermedad crónica: chequeo periódico de condición ya diagnosticada (hipertensión, diabetes, hipotiroidismo, etc.).

DATOS DEL PACIENTE (utilizar para personalizar la evaluación):
{{DATOS_DEL_PACIENTE}}

RED FLAGS – MANEJO ESCALONADO:

CRÍTICO ABSOLUTO — detener de inmediato y derivar (sin preguntas adicionales):
- Paro cardíaco o no respira en absoluto.
- Pérdida de conciencia activa o convulsiones en curso.
- Hemorragia masiva incontrolable.
- Anafilaxia con colapso (labios azules, hinchazón de garganta con incapacidad para respirar).
Respuesta: emergenciaDetectada:true, esUltimaPregunta:true, nivel 1. Instruir llamar al 911 de inmediato.

GRAVE PERO EVALUABLE — hacer 1-2 preguntas breves de severidad antes de finalizar:
- Dificultad respiratoria (puede hablar pero con esfuerzo).
- Dolor torácico intenso o súbito.
- Déficit neurológico agudo (parálisis, confusión severa, visión doble).
- Fiebre > 39°C con mal estado general.
- Sangrado activo preocupante.
- Embarazo con dolor o sangrado agudo.
Para estas: responder con emergenciaDetectada:true, esUltimaPregunta:false y hacer 1-2 preguntas
que evalúen: (a) intensidad actual en escala 1-10, (b) si mejora/empeora, (c) si puede hablar con
normalidad. Tras recibir las respuestas, emitir clasificación final con nivel 1.

INTERACTION STRATEGY:
FASE 1 – VALIDACIÓN DEL MOTIVO:
Antes de iniciar preguntas clínicas, valida que el motivo sea médicamente válido.
Son VÁLIDOS los siguientes tipos de consulta (pasar directamente a Fase 2):
  A) SÍNTOMA NUEVO: cualquier descripción de síntoma, molestia o malestar físico.
  B) SEGUIMIENTO: menciona "seguimiento", "control", "cita de control", "el médico me dijo que volviera",
     "revisión", "chequeo", o que fue indicada por un médico anteriormente.
  C) RENOVACIÓN DE RECETA: menciona "receta", "medicamento", "medicina", "se terminó", "se me acaba",
     "renovar", "pastillas", "tratamiento crónico", o nombre de medicamento específico.
  D) REVISIÓN DE RESULTADOS: menciona "resultados", "análisis", "laboratorio", "exámenes", "pruebas",
     "imágenes", "radiografía", "ecografía", o que tiene resultados para mostrar.
  E) CONTROL CRÓNICO: menciona condición crónica conocida (hipertensión, diabetes, hipotiroidismo,
     asma, artritis, etc.) en contexto de control periódico.

Son INVÁLIDOS (devolver sugerencias vacías y pedir aclaración):
  - Saludos solos sin motivo médico.
  - Preguntas sobre el sistema o costos.
  - Texto sin sentido o aleatorio.
  - Motivos claramente no médicos.

FASE 2 – PREGUNTAS ADAPTADAS AL TIPO DE CONSULTA (máximo 5 preguntas):
Identifica el tipo (A–E) y adapta las preguntas:

Para SÍNTOMA NUEVO (A):
  Evalúa: síntoma principal, duración, intensidad, progresión, factores asociados y antecedentes relevantes.

Para SEGUIMIENTO (B):
  Pregunta: (1) ¿De qué consulta o condición es el seguimiento? (2) ¿Cómo ha evolucionado desde la última cita?
  (3) ¿Tiene síntomas nuevos o ha empeorado alguno? (4) ¿Cumplió con indicaciones o tratamiento previo?
  Clasificar como nivel 4 o 5 salvo que reporte empeoramiento que amerite mayor prioridad.

Para RENOVACIÓN DE RECETA (C):
  Pregunta: (1) ¿Qué medicamento o tratamiento necesita renovar? (2) ¿Para qué condición lo toma?
  (3) ¿Hace cuánto tiempo lo usa y quién se lo prescribió? (4) ¿Ha tenido cambios en su estado de salud?
  (5) ¿Tiene síntomas nuevos o efectos adversos?
  Clasificar como nivel 4 o 5. Si hay efectos adversos preocupantes, elevar prioridad.

Para REVISIÓN DE RESULTADOS (D):
  Pregunta: (1) ¿Qué tipo de examen o análisis trae? (2) ¿Quién ordenó los exámenes y por qué?
  (3) ¿Tiene alguna preocupación o notó algo en los resultados? (4) ¿Ha tenido síntomas nuevos?
  Clasificar como nivel 4. Indicar en el resumen que el médico debe revisar resultados adjuntos.

Para CONTROL CRÓNICO (E):
  Pregunta: (1) ¿Qué condición crónica está controlando? (2) ¿Cómo ha estado su control últimamente?
  (3) ¿Ha tenido síntomas nuevos o descontrol? (4) ¿Está tomando su medicación regularmente?
  Clasificar como nivel 4 o 5 según estado de control reportado.

En todos los tipos:
  - Ofrece 2-4 opciones de respuesta en lenguaje sencillo (NUNCA vacías en Fase 2).
  - Puede finalizar antes de 5 preguntas si hay información suficiente.
  - Si en el transcurso aparecen síntomas nuevos preocupantes, escalar prioridad apropiadamente.

LANGUAGE AND STYLE:
- Lenguaje claro, clínico y neutral. Evitar alarmismo innecesario.
- Evitar términos técnicos complejos al hablar con el paciente.
- Profesional, empático y conciso.
- UNA SOLA PREGUNTA por turno. Nunca incluyas dos preguntas en el mismo mensaje. Si necesitas saber dos cosas, pregunta la más importante ahora y reserva la otra para el siguiente turno.
- Cuando el motivo es válido y pasas a Fase 2, omite cualquier confirmación del motivo. Ve directamente a la primera pregunta clínica sin preámbulo.
- BREVEDAD ESTRICTA: NO antepongas "Entendido.", "Gracias.", ni ningún resumen del turno anterior. NO repitas síntomas ya mencionados. Ve directo a la pregunta. La pregunta debe ser breve (máximo 80 caracteres).
- IMPORTANTE: Tu respuesta SIEMPRE debe ser únicamente el JSON indicado en OUTPUT FORMAT. Sin texto antes ni después del JSON. El JSON debe estar en UNA SOLA LÍNEA, sin saltos de línea ni espacios innecesarios.

VALIDACIÓN DE LA RESPUESTA DEL PACIENTE (Fase 2):
- Antes de avanzar a la siguiente pregunta, evalúa si la última respuesta del paciente realmente responde a la pregunta que hiciste.
- Si la respuesta es vacía, ininteligible, sin sentido, incoherente con la pregunta o claramente fuera de contexto: NO la consideres válida. Vuelve a formular la MISMA pregunta de forma más clara e incluye "repreguntar":true en el JSON. Esa respuesta NO se contará como una de las preguntas del triage.
- Si la respuesta es válida (aunque sea breve o imperfecta), continúa con la siguiente pregunta SIN incluir "repreguntar".
- Usa "repreguntar":true SOLO para respuestas realmente inválidas; nunca para respuestas válidas en las que simplemente quieras profundizar.

COHERENCIA CLÍNICA SEGÚN SEXO Y EDAD:
- Toma en cuenta el sexo y la edad del paciente (ver DATOS DEL PACIENTE). Si un síntoma o motivo reportado es biológicamente imposible o claramente incoherente con ese perfil (por ejemplo: embarazo, parto, lactancia o menstruación en un paciente de sexo masculino; síntomas exclusivos de adultos en un menor de muy corta edad; etc.), NO incorpores ese síntoma al análisis clínico: descártalo.
- En ese caso, informa al paciente de forma breve y respetuosa que ese dato no corresponde con su perfil registrado y pídele que lo confirme o lo corrija (por ejemplo: "Ese síntoma no corresponde con los datos registrados (sexo/edad). Por favor confirme o indique su molestia real."). Incluye "repreguntar":true para que ese turno NO cuente como una de las preguntas del triage.
- Usa tu propio juicio clínico para decidir qué es incoherente; no existe una lista fija de reglas.

OUTPUT FORMAT (JSON estricto — una sola línea, sin texto adicional antes o después):

Para solicitar aclaración del motivo (Fase 1, motivo no válido):
{"pregunta":"¿Podría describir el motivo de su consulta o los síntomas que presenta?","sugerencias":[],"esUltimaPregunta":false,"emergenciaDetectada":false}

Para preguntas clínicas (Fase 2):
{"pregunta":"¿Pregunta clínica directa?","sugerencias":["opción1","opción2","opción3"],"esUltimaPregunta":false,"emergenciaDetectada":false}

Para re-preguntar cuando la última respuesta es inválida/sin sentido (NO cuenta como pregunta respondida):
{"pregunta":"Reformula la MISMA pregunta de forma más clara.","sugerencias":["opción1","opción2","opción3"],"esUltimaPregunta":false,"emergenciaDetectada":false,"repreguntar":true}

Para red flag grave evaluable (primeras 1-2 preguntas de severidad):
{"pregunta":"¿Qué tan intenso es el malestar en escala del 1 al 10?","sugerencias":["Muy intenso (8-10)","Moderado (5-7)","Leve (1-4)","Puedo hablar normalmente"],"esUltimaPregunta":false,"emergenciaDetectada":true}

Para red flag crítica absoluta (o tras evaluar severidad grave):
{"pregunta":"Llame al 9-1-1 o acuda a urgencias de inmediato. No espere.","sugerencias":[],"esUltimaPregunta":true,"emergenciaDetectada":true}

Para evaluación final (tras recopilar información suficiente o alcanzar 5 preguntas):

Instrucciones para los campos "observaciones" y "resumen":
- "observaciones": UNA SOLA frase clínica breve. Máximo 120 caracteres. Incluye: motivo, síntoma principal, duración y alertas si existen. Sin saltos de línea.
- "resumen": formato SOAP-S, tercera persona, sin "el paciente indicó/dijo". Usa: "Consulta por...", "Presenta...". Incluye antecedentes SOLO si son clínicamente relevantes; si no hay antecedentes relevantes, NO escribas frases de relleno como "Sin antecedentes de importancia", "Sin antecedentes relevantes" ni "Antecedentes: ninguno" — simplemente OMÍTELOS. Máximo 2 oraciones. Máximo 280 caracteres. Sin saltos de línea.

{"esFinal":true,"clasificacion":{"nivel":4,"nombre":"Estándar","confianza":"alta"},"observaciones":"Motivo, síntoma principal, duración y alertas clínicas si existen.","resumen":"Paciente consulta por [síntoma] de [duración]. Intensidad [nivel]. [Síntomas asociados]."}

NIVELES DE PRIORIDAD Y DERIVACIÓN:
1 = Emergencia (riesgo vital — NO apto para teleconsulta, derivar a urgencias o llamar al 911)
2 = Urgente (alto riesgo — NO apto para teleconsulta, derivar a instalación hospitalaria más cercana para atención presencial)
3 = Prioritario (atención pronta — NO apto para teleconsulta, derivar a centro de salud más cercano para atención presencial)
4 = Estándar (consulta general — apto para teleconsulta)
5 = No urgente (preventiva/informativa — apto para teleconsulta)

REGLA DE DERIVACIÓN:
Solo los niveles 4 y 5 son aptos para teleconsulta.
Los niveles 1, 2 y 3 deben ser derivados a atención presencial según su gravedad.

FINAL RULE:
El médico humano siempre tiene la decisión final.
Tu rol es organizar, priorizar y asistir — nunca sustituir al criterio clínico.`;

/* Instrucciones por defecto del resumen para el paciente.
   Copia de INSTRUCCIONES_RESUMEN_DEFAULT en
   functions/src/consultas/resumenPaciente.ts — si se cambia allá, cambiarla aquí:
   este texto es el que se ofrece como "restaurar por defecto" en el editor. */
const DEFAULT_RESUMEN_PACIENTE_PROMPT = `Eres un médico que escribe un mensaje BREVE, cálido y personalizado para tu paciente, que lo leerá en la app móvil del Ministerio de Salud (MINSA Panamá).

Escribe en ESPAÑOL, en segunda persona (trato cercano y respetuoso), claro y SIN tecnicismos médicos.

REGLAS IMPORTANTES:
- NO incluyas diagnóstico, nombres de enfermedades, ni información sensible o que pueda confundir o asustar al paciente.
- NO uses frases clínicas como "el paciente refiere", "paciente masculino", "tos seca de X días". Háblale directamente.
- Usa SOLO la información provista; NO inventes medicamentos, exámenes ni indicaciones. Si algo no fue indicado, omite esa sección.
- COBERTURA COMPLETA (obligatorio): si la lista de medicamentos no está vacía, DEBES nombrar TODOS y CADA UNO de ellos con su dosis y frecuencia. Si la lista de exámenes/órdenes no está vacía, DEBES nombrar TODOS y CADA UNO. Omitir uno solo deja al paciente sin saber qué tomar o qué examen hacerse: es el error más grave posible en este mensaje.
- Antes de responder, relee tu mensaje y verifica que aparezcan todos los medicamentos y todos los exámenes de las listas.
- Sé breve, concreto y amable — pero la brevedad NUNCA justifica dejar fuera un medicamento o un examen.

ESTRUCTURA del mensaje:
1. Saludo: "Estimado(a) {{pacienteNombre}},"
2. Una o dos frases sobre la consulta de hoy y lo que acordaron (puedes mencionar el motivo en lenguaje sencillo).
3. Si hay medicamentos: indícalos con su dosis de forma simple (ej. "te receté acetaminofén 500 mg, una cada 8 horas").
4. Si hay exámenes/órdenes: indícale que se realice esos exámenes.
5. Una línea que empiece con "Seguimiento:" con la indicación de control/seguimiento (si aplica).
6. Una línea que empiece con "Educación:" con recomendaciones de cuidado en casa (si aplica).`;

const PROMPT_CONFIG: Record<PromptType, { label: string; docPath: string; description: string; chatWelcome: string; defaultTemplate?: string; soportaChat: boolean }> = {
  triage: {
    label: 'Triage',
    docPath: 'config/triagePrompt',
    description: 'Prompt del sistema de triage AI para la app móvil del paciente.',
    chatWelcome: '¡Hola! Soy el asistente de triage. ¿Cuál es el motivo de su consulta hoy?',
    soportaChat: true,
    defaultTemplate: DEFAULT_TRIAGE_PROMPT,
  },
  consultation: {
    label: 'Consulta médica',
    docPath: 'config/consultationPrompt',
    description: 'Prompt para generar notas SOAP durante la teleconsulta. Usa {{CONTEXTO}} y {{TRANSCRIPCION}} como placeholders.',
    chatWelcome: 'Soy el asistente de notas médicas. Envía un fragmento de transcripción para probar cómo genera la nota SOAP.',
    soportaChat: true,
    defaultTemplate: DEFAULT_CONSULTATION_PROMPT,
  },
  resumenPaciente: {
    label: 'Resumen para el paciente',
    docPath: 'config/resumenPacientePrompt',
    description:
      'Instrucciones con las que la IA redacta el mensaje que el paciente lee en la app al terminar la teleconsulta: qué información incluir, con qué tono y en qué orden. Aquí NO se edita el mensaje de una consulta concreta — ese solo lo cambia el médico durante la consulta. Marcador disponible: {{pacienteNombre}}. Los datos de la consulta (motivo, plan, medicamentos, órdenes) los agrega siempre el sistema y no se pueden quitar desde aquí.',
    chatWelcome: '',
    defaultTemplate: DEFAULT_RESUMEN_PACIENTE_PROMPT,
    soportaChat: false,
  },
};

/* ═══════════════════════════════════════════════════════════════
   Helpers
   ═══════════════════════════════════════════════════════════════ */

function formatTimestamp(ts?: unknown): string {
  if (!ts) return '—';
  let d: Date;
  if (typeof ts === 'object' && ts !== null && 'toDate' in ts && typeof (ts as Record<string, unknown>).toDate === 'function') {
    d = (ts as { toDate: () => Date }).toDate();
  } else if (ts instanceof Date) {
    d = ts;
  } else if (typeof ts === 'number') {
    d = new Date(ts);
  } else if (typeof ts === 'string') {
    d = new Date(ts);
  } else if (typeof ts === 'object' && ts !== null && 'seconds' in ts) {
    d = new Date((ts as { seconds: number }).seconds * 1000);
  } else {
    return '—';
  }
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('es-CR', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/* ═══════════════════════════════════════════════════════════════
   Triage chat — SIEMPRE vía Cloud Function portal_probarTriageChat.
   La key de Gemini NO se expone al cliente (SEC-03): el backend la
   maneja con Secret Manager (defineSecret('GEMINI_API_KEY')).
   ═══════════════════════════════════════════════════════════════ */

const probarTriageChatFn = httpsCallable<
  { messages: Array<{ role: string; text: string }>; promptOverride?: string },
  { success: boolean; response: string }
>(functions, 'portal_probarTriageChat');

/* ═══════════════════════════════════════════════════════════════
   Component
   ═══════════════════════════════════════════════════════════════ */

export default function AIPromptsPage() {
  const { user } = useAuth();
  const { addToast } = useToast();

  // ─── State ────────────────────────────────────────────────
  const [promptType, setPromptType] = useState<PromptType>('triage');
  const promptCfg = PROMPT_CONFIG[promptType];
  const [activeTab, setActiveTab] = useState<TabId>('editor');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Prompt data
  const [originalPrompt, setOriginalPrompt] = useState('');
  const [draftPrompt, setDraftPrompt] = useState('');
  const [promptMeta, setPromptMeta] = useState<Partial<TriagePromptDoc>>({});

  // Versions
  const [versions, setVersions] = useState<VersionDoc[]>([]);
  const [selectedVersion, setSelectedVersion] = useState<VersionDoc | null>(null);
  const [loadingVersions, setLoadingVersions] = useState(false);

  // Save modal
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [saveNote, setSaveNote] = useState('');

  // Restore modal
  const [showRestoreModal, setShowRestoreModal] = useState(false);
  const [versionToRestore, setVersionToRestore] = useState<VersionDoc | null>(null);

  // Chat
  const [chatMessages, setChatMessages] = useState<ChatMessage[]>([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [chatStarted, setChatStarted] = useState(false);
  const chatEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const lineNumbersRef = useRef<HTMLDivElement>(null);

  // Resize
  const [editorWidth, setEditorWidth] = useState<number | null>(null);
  const [isResizing, setIsResizing] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Estándar de cambios sin guardar. Se conserva el nombre `hasChanges` porque
  // ya lo usan el botón Revertir y el de Guardar. Respecto al cálculo anterior
  // (draftPrompt !== originalPrompt) suma el aviso al cerrar/recargar la pestaña.
  const { hayCambios: hasChanges, marcarGuardado } = useCambiosSinGuardar(draftPrompt, {
    habilitado: !loading,
  });
  // originalPrompt es la línea base: cambia al cargar, guardar o restaurar versión.
  useEffect(() => {
    marcarGuardado(originalPrompt);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [originalPrompt]);

  // ─── Load prompt ──────────────────────────────────────────

  useEffect(() => {
    // Reset state before loading new prompt type
    setOriginalPrompt('');
    setDraftPrompt('');
    setPromptMeta({});
    setVersions([]);
    setSelectedVersion(null);
    setLoading(true);

    async function load() {
      try {
        const [configName, docName] = promptCfg.docPath.split('/');
        const snap = await getDoc(doc(db, configName, docName));
        if (snap.exists()) {
          const raw = snap.data();
          console.log(`[AIPrompts] Documento ${promptCfg.docPath} cargado, campos:`, Object.keys(raw));

          const text = (raw.template as string) || '';
          const version =
            typeof raw.version === 'number'
              ? raw.version
              : Number(raw.version) || 0;

          setOriginalPrompt(text);
          setDraftPrompt(text);
          setPromptMeta({
            contenido: text,
            version,
            actualizadoEn: raw.updatedAt ?? undefined,
            actualizadoPor: raw.updatedBy ?? undefined,
          });
        } else {
          console.warn(`[AIPrompts] Documento ${promptCfg.docPath} no existe.`);
          // Auto-seed with default template if available
          if (promptCfg.defaultTemplate) {
            console.log(`[AIPrompts] Sembrando prompt por defecto para ${promptCfg.label}...`);
            const [cn, dn] = promptCfg.docPath.split('/');
            await setDoc(doc(db, cn, dn), {
              template: promptCfg.defaultTemplate,
              version: 1,
              updatedAt: serverTimestamp(),
              updatedBy: user?.email || 'system',
              description: `Prompt de ${promptCfg.label} - generado automáticamente.`,
            });
            setOriginalPrompt(promptCfg.defaultTemplate);
            setDraftPrompt(promptCfg.defaultTemplate);
            setPromptMeta({ version: 1 });
            addToast('success', `Prompt de ${promptCfg.label} creado con la plantilla por defecto.`);
          } else {
            setOriginalPrompt('');
            setDraftPrompt('');
            setPromptMeta({ version: 0 });
          }
        }
      } catch (err) {
        console.error('Error cargando prompt:', err);
        addToast('error', `Error al cargar el prompt de ${promptCfg.label}.`);
      } finally {
        setLoading(false);
      }
    }
    load();
    // Reset chat when switching prompt type
    setChatMessages([]);
    setChatStarted(false);
  }, [promptType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Load versions ────────────────────────────────────────

  const loadVersions = useCallback(async () => {
    setLoadingVersions(true);
    try {
      const [colName, docName] = promptCfg.docPath.split('/');
      const q = query(
        collection(db, colName, docName, 'versiones'),
        orderBy('version', 'desc'),
      );
      const snap = await getDocs(q);
      const docs = snap.docs.map((d) => ({
        id: d.id,
        ...(d.data() as Omit<VersionDoc, 'id'>),
      }));
      setVersions(docs);
      if (docs.length > 0 && !selectedVersion) {
        setSelectedVersion(docs[0]);
      }
    } catch (err) {
      console.error('Error cargando versiones:', err);
    } finally {
      setLoadingVersions(false);
    }
  }, [promptType]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (activeTab === 'versions') {
      loadVersions();
    }
  }, [activeTab, promptType]); // eslint-disable-line react-hooks/exhaustive-deps

  // ─── Sync line numbers scroll ──────────────────────────────

  const handleEditorScroll = useCallback(() => {
    if (textareaRef.current && lineNumbersRef.current) {
      lineNumbersRef.current.scrollTop = textareaRef.current.scrollTop;
    }
  }, []);

  // ─── Resize handle drag ───────────────────────────────────

  const handleResizeStart = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      setIsResizing(true);

      const startX = e.clientX;
      const startWidth =
        editorWidth ?? containerRef.current?.firstElementChild?.getBoundingClientRect().width ?? 0;

      const onMove = (ev: MouseEvent) => {
        const container = containerRef.current;
        if (!container) return;
        const containerWidth = container.getBoundingClientRect().width;
        const handleWidth = 8;
        const delta = ev.clientX - startX;
        const newWidth = Math.max(
          300,
          Math.min(startWidth + delta, containerWidth - 280 - handleWidth),
        );
        setEditorWidth(newWidth);
      };

      const onUp = () => {
        setIsResizing(false);
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      };

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [editorWidth],
  );

  // ─── Auto-scroll chat ─────────────────────────────────────

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages, chatLoading]);

  // ─── Save prompt ──────────────────────────────────────────

  const handleSave = async () => {
    if (!hasChanges) return;
    setSaving(true);
    try {
      const newVersion = (promptMeta.version || 0) + 1;

      const [colName, docName] = promptCfg.docPath.split('/');

      // Save current as version in subcollection before overwriting
      if (originalPrompt) {
        await addDoc(collection(db, colName, docName, 'versiones'), {
          contenido: originalPrompt,
          version: promptMeta.version || 0,
          creadoEn: promptMeta.actualizadoEn || serverTimestamp(),
          creadoPor: promptMeta.actualizadoPor || 'desconocido',
          nota: saveNote.trim(),
        });
      }

      // Update main document — write both "prompt" (mobile reads) and "contenido"
      await setDoc(doc(db, colName, docName), {
        template: draftPrompt,
        version: newVersion,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || 'admin',
        description: 'Triage AI system prompt template. Edit via Firebase Console or future web admin.',
      });

      setOriginalPrompt(draftPrompt);
      setPromptMeta((prev) => ({ ...prev, version: newVersion }));
      setShowSaveModal(false);
      setSaveNote('');
      addToast('success', `Prompt actualizado a versión ${newVersion}.`);
    } catch (err) {
      console.error('Error guardando prompt:', err);
      addToast('error', 'Error al guardar el prompt.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Restore version ──────────────────────────────────────

  const handleRestore = async () => {
    if (!versionToRestore) return;
    setSaving(true);
    try {
      const newVersion = (promptMeta.version || 0) + 1;

      const [colName, docName] = promptCfg.docPath.split('/');

      // Save current as version before restoring
      if (originalPrompt) {
        await addDoc(collection(db, colName, docName, 'versiones'), {
          contenido: originalPrompt,
          version: promptMeta.version || 0,
          creadoEn: serverTimestamp(),
          creadoPor: user?.email || 'admin',
          nota: `Respaldo antes de restaurar a v${versionToRestore.version}`,
        });
      }

      // Update main document with restored version
      await setDoc(doc(db, colName, docName), {
        template: versionToRestore.contenido,
        version: newVersion,
        updatedAt: serverTimestamp(),
        updatedBy: user?.email || 'admin',
        description: 'Triage AI system prompt template. Edit via Firebase Console or future web admin.',
      });

      setOriginalPrompt(versionToRestore.contenido);
      setDraftPrompt(versionToRestore.contenido);
      setPromptMeta((prev) => ({ ...prev, version: newVersion }));
      setShowRestoreModal(false);
      setVersionToRestore(null);
      addToast('success', `Prompt restaurado desde versión ${versionToRestore.version}.`);
      await loadVersions();
    } catch (err) {
      console.error('Error restaurando versión:', err);
      addToast('error', 'Error al restaurar la versión.');
    } finally {
      setSaving(false);
    }
  };

  // ─── Chat logic (always uses draftPrompt) ──────────────────

  const startChat = () => {
    setChatMessages([
      {
        id: 'welcome',
        role: 'bot',
        text: promptCfg.chatWelcome,
      },
    ]);
    setChatStarted(true);
  };

  const resetChat = () => {
    setChatMessages([]);
    setChatStarted(false);
    setChatInput('');
  };

  /** Send a text string to the chat (used by form submit + suggestion clicks). */
  const sendToChat = useCallback(
    async (text: string, currentMessages: ChatMessage[]) => {
      const userMsg: ChatMessage = {
        id: `user_${Date.now()}`,
        role: 'user',
        text,
      };
      const allMessages = [...currentMessages, userMsg];
      setChatMessages(allMessages);
      setChatInput('');
      setChatLoading(true);

      try {
        const mapped = allMessages.map((m) => ({
          role: m.role === 'bot' ? 'assistant' : 'user',
          text: m.text,
        }));

        const result = await probarTriageChatFn({
          messages: mapped,
          promptOverride: draftPrompt,
        });
        const responseText = result.data.response;

        const parsed = parseBotResponse(responseText);
        setChatMessages((prev) => [
          ...prev,
          {
            id: `bot_${Date.now()}`,
            role: 'bot',
            text: parsed.text,
            suggestions: parsed.suggestions,
            isSummary: parsed.isSummary,
          },
        ]);
      } catch (err: unknown) {
        const fbErr = err as { message?: string };
        addToast('error', fbErr.message || 'Error al comunicarse con el servicio de triage.');
      } finally {
        setChatLoading(false);
      }
    },
    [draftPrompt, addToast],
  );

  const sendChatMessage = async (e?: FormEvent) => {
    e?.preventDefault();
    if (!chatInput.trim() || chatLoading) return;
    sendToChat(chatInput.trim(), chatMessages);
  };

  const handleChatKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendChatMessage();
    }
  };

  // ─── Render helpers ───────────────────────────────────────

  const lineCount = draftPrompt.split('\n').length;

  if (loading) {
    return (
      <MainLayout>
        <div className={styles.loading}>
          <Loader2 size={20} className={styles.spinner} />
          Cargando prompt...
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout>
      <div className={styles.page}>
        {/* ─── Header ─────────────────────────────────────── */}
        <div className={styles.header}>
          <Bot size={22} />
          <h1 className={styles.title}>Prompts IA</h1>
          {/* Prompt type selector */}
          <div className={styles.promptTypeSelector}>
            {(Object.keys(PROMPT_CONFIG) as PromptType[]).map((type) => (
              <button
                key={type}
                type="button"
                className={`${styles.promptTypeBtn} ${promptType === type ? styles.promptTypeBtnActive : ''}`}
                onClick={() => setPromptType(type)}
              >
                {PROMPT_CONFIG[type].label}
              </button>
            ))}
          </div>
          <div className={styles.headerActions}>
            {promptMeta.version != null && (
              <span className={styles.editorMeta}>
                <strong>v{promptMeta.version}</strong>
              </span>
            )}
          </div>
        </div>
        <p className={styles.promptDescription}>{promptCfg.description}</p>

        {/* ─── Tabs ───────────────────────────────────────── */}
        <div className={styles.tabs}>
          {([
            { id: 'editor' as TabId, label: 'Editor', icon: Code2 },
            { id: 'versions' as TabId, label: 'Versiones', icon: History },
          ]).map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              type="button"
              className={`${styles.tab} ${activeTab === id ? styles.tabActive : ''}`}
              onClick={() => setActiveTab(id)}
            >
              <Icon size={14} />
              {label}
            </button>
          ))}
        </div>

        {/* ─── Tab Content ────────────────────────────────── */}
        <div className={styles.contentArea}>
          {/* ═══ EDITOR + CHAT TAB ═══ */}
          {activeTab === 'editor' && (
            <div className={styles.editorChatLayout} ref={containerRef}>
              {/* ── Left: Editor ── */}
              <div
                className={styles.editorPanel}
                // Sin chat al lado, el editor ocupa todo el ancho: el arrastre de
                // redimensionar solo tiene sentido cuando hay dos paneles.
                style={
                  !promptCfg.soportaChat
                    ? { flex: 1 }
                    : editorWidth
                      ? { width: editorWidth, flexShrink: 0, flexGrow: 0 }
                      : { flex: 2 }
                }
              >
                <div className={styles.editorCard}>
                  {/* Toolbar */}
                  <div className={styles.editorToolbar}>
                    <div className={styles.editorToolbarLeft}>
                      <span className={styles.editorMeta}>
                        <strong>{lineCount}</strong> líneas
                      </span>
                      {promptMeta.actualizadoPor && (
                        <span className={styles.editorMeta}>
                          Editado por <strong>{promptMeta.actualizadoPor}</strong>
                        </span>
                      )}
                      {promptMeta.actualizadoEn && (
                        <span className={styles.editorMeta}>
                          {formatTimestamp(promptMeta.actualizadoEn)}
                        </span>
                      )}
                    </div>
                    <div className={styles.editorToolbarRight}>
                      <BadgeCambiosSinGuardar visible={hasChanges} />
                    </div>
                  </div>

                  {/* Editor */}
                  <div className={styles.editorWrap}>
                    <div className={styles.lineNumbers} ref={lineNumbersRef}>
                      {Array.from({ length: lineCount }, (_, i) => (
                        <div key={i} className={styles.lineNumber}>
                          {i + 1}
                        </div>
                      ))}
                    </div>
                    <textarea
                      ref={textareaRef}
                      className={styles.editorTextarea}
                      value={draftPrompt}
                      onChange={(e) => setDraftPrompt(e.target.value)}
                      onScroll={handleEditorScroll}
                      placeholder={`Escriba el prompt de ${promptCfg.label} aquí...`}
                      spellCheck={false}
                    />
                  </div>

                  {/* Actions */}
                  <div className={styles.editorActions}>
                    <div className={styles.editorActionsLeft}>
                      {hasChanges && (
                        <button
                          type="button"
                          className={styles.btnGhost}
                          onClick={() => setDraftPrompt(originalPrompt)}
                        >
                          <RotateCcw size={13} />
                          Descartar cambios
                        </button>
                      )}
                    </div>
                    <div className={styles.editorActionsRight}>
                      <button
                        type="button"
                        className={styles.btnPrimary}
                        disabled={!hasChanges || saving}
                        onClick={() => setShowSaveModal(true)}
                      >
                        <Save size={14} />
                        {saving ? 'Guardando...' : 'Guardar prompt'}
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              {/* El chat de prueba es conversacional (portal_probarTriageChat).
                  El resumen del paciente no lo es: se genera de una sola vez a
                  partir de los datos de la consulta, así que ahí no se muestra. */}
              {promptCfg.soportaChat && (
                <>
                {/* ── Resize Handle ── */}
                <div
                  className={`${styles.resizeHandle} ${isResizing ? styles.resizeHandleActive : ''}`}
                  onMouseDown={handleResizeStart}
                />

                {/* ── Right: Chat ── */}
                <div className={styles.chatPanel} style={{ flex: 1 }}>
                  <div className={styles.chatHeader}>
                    <span className={styles.chatHeaderTitle}>
                      <MessageSquare size={15} />
                      Probar triage
                    </span>
                    <div className={styles.chatHeaderActions}>
                      {chatStarted && (
                        <button
                          type="button"
                          className={styles.btnGhost}
                          onClick={resetChat}
                        >
                          <RotateCcw size={13} />
                          Reiniciar
                        </button>
                      )}
                      {!chatStarted && (
                        <button
                          type="button"
                          className={styles.btnPrimary}
                          disabled={!draftPrompt}
                          onClick={startChat}
                          style={{ height: 32, fontSize: 'var(--font-size-xs)' }}
                        >
                          <MessageSquare size={13} />
                          Iniciar
                        </button>
                      )}
                    </div>
                  </div>

                  <div className={styles.chatMessages}>
                    {!chatStarted ? (
                      <div className={styles.emptyState}>
                        <MessageSquare size={36} />
                        <p className={styles.emptyStateText}>
                          Inicie el chat para probar el prompt con una conversación
                          simulada de triage. Los cambios en el editor se aplican
                          inmediatamente.
                        </p>
                      </div>
                    ) : (
                      <>
                        {chatMessages.map((msg) => (
                          <div
                            key={msg.id}
                            className={`${styles.chatMessage} ${
                              msg.role === 'bot'
                                ? styles.chatMessageBot
                                : styles.chatMessageUser
                            }`}
                          >
                            <span className={styles.chatSender}>
                              {msg.role === 'bot' ? 'Triage Bot' : 'Paciente'}
                            </span>
                            {msg.isSummary ? (
                              <div className={styles.chatSummary}>
                                <p className={styles.chatSummaryTitle}>Resumen de triage</p>
                                <p className={styles.chatSummaryContent}>{msg.text}</p>
                              </div>
                            ) : (
                              <div className={styles.chatBubble}>{msg.text}</div>
                            )}
                            {msg.suggestions && msg.suggestions.length > 0 && (
                              <div className={styles.chatSuggestions}>
                                {msg.suggestions.map((s) => (
                                  <button
                                    key={s}
                                    type="button"
                                    className={styles.chatSuggestionBtn}
                                    disabled={chatLoading}
                                    onClick={() => sendToChat(s, chatMessages)}
                                  >
                                    {s}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                        {chatLoading && (
                          <div className={`${styles.chatMessage} ${styles.chatMessageBot}`}>
                            <span className={styles.chatSender}>Triage Bot</span>
                            <div className={styles.typingIndicator}>
                              <span className={styles.typingDot} />
                              <span className={styles.typingDot} />
                              <span className={styles.typingDot} />
                            </div>
                          </div>
                        )}
                        <div ref={chatEndRef} />
                      </>
                    )}
                  </div>

                  {chatStarted && (
                    <form className={styles.chatInputArea} onSubmit={sendChatMessage}>
                      <input
                        type="text"
                        className={styles.chatInput}
                        value={chatInput}
                        onChange={(e) => setChatInput(e.target.value)}
                        onKeyDown={handleChatKeyDown}
                        placeholder="Escriba su mensaje..."
                        disabled={chatLoading}
                      />
                      <button
                        type="submit"
                        className={styles.chatSendBtn}
                        disabled={!chatInput.trim() || chatLoading}
                      >
                        <Send size={16} />
                      </button>
                    </form>
                  )}
                </div>
                </>
              )}
            </div>
          )}

          {/* ═══ VERSIONS TAB ═══ */}
          {activeTab === 'versions' && (
            <div className={styles.versionsLayout}>
              {/* Version list */}
              <div className={styles.versionsList}>
                <div className={styles.versionsHeader}>
                  <History size={15} />
                  Historial de versiones
                </div>
                <div className={styles.versionsScrollArea}>
                  {/* Current version (always first) */}
                  <div
                    className={`${styles.versionItem} ${styles.versionItemCurrent} ${
                      !selectedVersion ? styles.versionItemActive : ''
                    }`}
                    onClick={() => setSelectedVersion(null)}
                  >
                    <div className={styles.versionItemHeader}>
                      <span className={styles.versionLabel}>
                        Versión {promptMeta.version || 0}
                      </span>
                      <span className={styles.versionBadge}>Actual</span>
                    </div>
                    <span className={styles.versionMeta}>
                      {promptMeta.actualizadoPor || '—'} ·{' '}
                      {formatTimestamp(promptMeta.actualizadoEn )}
                    </span>
                  </div>

                  {loadingVersions ? (
                    <div className={styles.loading}>
                      <Loader2 size={16} className={styles.spinner} />
                    </div>
                  ) : versions.length === 0 ? (
                    <div className={styles.emptyVersions}>
                      No hay versiones anteriores
                    </div>
                  ) : (
                    versions.map((v) => (
                      <div
                        key={v.id}
                        className={`${styles.versionItem} ${
                          selectedVersion?.id === v.id ? styles.versionItemActive : ''
                        }`}
                        onClick={() => setSelectedVersion(v)}
                      >
                        <div className={styles.versionItemHeader}>
                          <span className={styles.versionLabel}>
                            Versión {v.version}
                          </span>
                        </div>
                        <span className={styles.versionMeta}>
                          {v.creadoPor || '—'} · {formatTimestamp(v.creadoEn )}
                        </span>
                        {v.nota && (
                          <span className={styles.versionNote}>{v.nota}</span>
                        )}
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Version preview */}
              <div className={styles.versionPreview}>
                <div className={styles.versionPreviewHeader}>
                  <span className={styles.versionPreviewTitle}>
                    {selectedVersion
                      ? `Versión ${selectedVersion.version}`
                      : `Versión ${promptMeta.version || 0} (actual)`}
                  </span>
                  {selectedVersion && (
                    <button
                      type="button"
                      className={styles.btnSecondary}
                      onClick={() => {
                        setVersionToRestore(selectedVersion);
                        setShowRestoreModal(true);
                      }}
                    >
                      <ArrowDownToLine size={14} />
                      Restaurar esta versión
                    </button>
                  )}
                </div>
                <div className={styles.versionPreviewContent}>
                  {selectedVersion
                    ? selectedVersion.contenido
                    : originalPrompt || 'No hay prompt configurado.'}
                </div>
              </div>
            </div>
          )}

        </div>
      </div>

      {/* ═══ SAVE MODAL ═══ */}
      <Modal
        open={showSaveModal}
        onClose={() => setShowSaveModal(false)}
        title="Guardar prompt"
      >
        <div className={styles.saveModal}>
          <p className={styles.saveModalText}>
            Se guardará la nueva versión del prompt y la versión actual se archivará en
            el historial. Describa los cambios realizados (mínimo 50 caracteres).
          </p>
          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>
              Descripción del cambio *
            </label>
            <textarea
              className={styles.fieldTextarea}
              value={saveNote}
              onChange={(e) => setSaveNote(e.target.value)}
              placeholder="Ej: Se agregaron preguntas sobre alergias medicamentosas y se ajustó el flujo de urgencias para incluir validación de síntomas respiratorios..."
              rows={4}
            />
            <span
              className={styles.editorMeta}
              style={{
                color:
                  saveNote.trim().length > 0 && saveNote.trim().length < 50
                    ? 'var(--color-danger, #dc2626)'
                    : undefined,
              }}
            >
              {saveNote.trim().length}/50 caracteres mínimo
            </span>
          </div>
          <div className={styles.saveModalActions}>
            <Button variant="secondary" onClick={() => setShowSaveModal(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              disabled={saving || saveNote.trim().length < 50}
            >
              <Save size={14} />
              {saving ? 'Guardando...' : 'Guardar'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ═══ RESTORE MODAL ═══ */}
      <Modal
        open={showRestoreModal}
        onClose={() => setShowRestoreModal(false)}
        title="Restaurar versión"
      >
        <div className={styles.saveModal}>
          <div className={styles.restoreWarning}>
            <AlertTriangle size={18} />
            <p className={styles.restoreWarningText}>
              La versión actual del prompt se respaldará automáticamente en el historial
              antes de restaurar la versión {versionToRestore?.version}.
            </p>
          </div>
          <p className={styles.saveModalText}>
            ¿Desea restaurar el prompt a la versión {versionToRestore?.version}?
          </p>
          <div className={styles.saveModalActions}>
            <Button
              variant="secondary"
              onClick={() => setShowRestoreModal(false)}
            >
              Cancelar
            </Button>
            <Button
              variant="primary"
              onClick={handleRestore}
              disabled={saving}
            >
              <ArrowDownToLine size={14} />
              {saving ? 'Restaurando...' : 'Restaurar'}
            </Button>
          </div>
        </div>
      </Modal>
    </MainLayout>
  );
}
